<script setup lang="ts">
import gsap from 'gsap';

import { BasicShadowMap, NoToneMapping, SRGBColorSpace, Vector3 } from 'three';

import { type TresContext, TresCanvas } from '@tresjs/core';

import { Ocean, Sky } from '@tresjs/cientos';

import { perfLifecycle } from 'src/composables/utils/perfMarks';

import { useYandexMetrika } from 'yandex-metrika-vue3';

import Lenis from 'lenis';

import waterNormals from 'src/assets/textures/Water_1_M_Normal.avif';

const { enableOcean, enableSky, antialias, dprCap, tier } = use3DQuality();

const yandexMetrika = useYandexMetrika();
const router = useRouter();
const route = useRoute();

const isClient = ref(false);

const waterLikePostEffectDS3Ref = ref();

const gl = {
  antialias: antialias.value,
  dpr: dprCap.value,
  windowSize: true,
  clearColor: '#c5aaff',
  alpha: false,
  shadowMapType: BasicShadowMap,
  outputColorSpace: SRGBColorSpace,
  toneMapping: NoToneMapping,
};

const cameraZoomDefault = 0.5;
const cameraZoomMax = 1.0;

// Прогресс приближения мыши к кнопке (0 = далеко, 1 = над кнопкой)
const hoverProgress = ref(0);
provide('hoverProgress', hoverProgress);

const wheel = ref(0);

let lenisInstance: Lenis | null = null;
let lenisRafId = 0;

const initLenis = () => {
  const lenis = new Lenis({
    infinite: true,
  });

  function raf(time: number) {
    lenis.raf(time);
    lenisRafId = requestAnimationFrame(raf);
  }

  lenisRafId = requestAnimationFrame(raf);
  lenisInstance = lenis;

  lenis.on('scroll', (e: Lenis) => {
    const { velocity } = e;

    wheel.value += velocity;

    const zoom = 1 - velocity / 10;

    if (zoom >= 0.1 && zoom <= 2) {
      gsap.set(perspectiveCameraRef.value, { zoom });
    }
  });
};

const canvasRef = ref();

let context: TresContext;
let cleanupCameraFollow: (() => void) | null = null;
let gsapCtx: gsap.Context;

const skyPropsDefaults = () => ({
  turbidity: 1,
  rayleigh: 2,
  elevation: 1.2,
  azimuth: 183,
  mieCoefficient: 0.005,
  mieDirectionalG: 0.8,
  distance: 10000,
});

const skyProps = reactive(skyPropsDefaults());

onMounted(async () => {
  isClient.value = true;

  await nextTick();

  perfLifecycle.scene3dStart('index');

  context = canvasRef.value.context;

  cleanupCameraFollow = useTreCameraFollow(context, { shiftY: 0.4 });

  gsapCtx = gsap.context(() => {
    // GSAP tweens for mouseenter/mouseleave/goToProjects
    // are created inside this context for proper cleanup
  });

  perfLifecycle.scene3dEnd('index');

  // WebGL context guard — защита от чёрного экрана при потере контекста
  useWebGLContextGuard(
    () => context.renderer.value?.domElement ?? null,
    () => { console.warn('[TreIndex] WebGL context lost'); },
    () => { console.log('[TreIndex] WebGL context restored'); },
  );

  // initLenis();
});

onUnmounted(() => {
  cleanupCameraFollow?.();
  cleanupCameraFollow = null;

  gsapCtx?.revert();

  if (lenisRafId) cancelAnimationFrame(lenisRafId);
  lenisInstance?.destroy();
  lenisInstance = null;
});

const perspectiveCameraRef = ref();
provide('cameraRef', perspectiveCameraRef);

// < Camera Zoom

let isMouseOver = false;
const durationLeave = 2;

const mouseenter = () => {
  if (isMouseOver) return;

  isMouseOver = true;
  hoverProgress.value = 1;

  gsapCtx.add(() => {
    gsap.to(perspectiveCameraRef.value, {
      zoom: cameraZoomMax,
      duration: durationLeave,
      ease: 'power1.out',
    });

    gsap.to(skyProps, {
      elevation: 2,
      duration: durationLeave,
      ease: 'power1.out',
    });

    gsap.to(skyProps, {
      azimuth: 179,
      duration: durationLeave,
      ease: 'power1.out',
    });
  });
};

const mouseleave = () => {
  isMouseOver = false;
  hoverProgress.value = 0;

  gsapCtx.add(() => {
    gsap.to(perspectiveCameraRef.value, {
      zoom: cameraZoomDefault,
      duration: durationLeave,
      ease: 'power1.out',
    });

    gsap.to(skyProps, {
      elevation: skyPropsDefaults().elevation,
      duration: durationLeave,
      ease: 'power1.out',
    });

    gsap.to(skyProps, {
      azimuth: skyPropsDefaults().azimuth,
      duration: durationLeave,
      ease: 'power1.out',
    });
  });
};

// >

const goToProjects = () => {
  yandexMetrika.reachGoal('click_btn_view_works');

  const duration = 0.6;

  gsapCtx.add(() => {
    gsap.to(perspectiveCameraRef.value.rotation, {
      x: Math.PI * 0.5,
      duration,
      onComplete: () => { router.push('/projects') },
    });
  });
};

const btnVariantMap: Record<string, 'default' | 'glass' | 'shadow' | 'neon' | 'shimmer' | 'starry'> = {
  '1': 'glass',
  '2': 'shadow',
  '3': 'neon',
  '4': 'shimmer',
  '5': 'starry',
};

const btnVariant = computed(() => {
  const btn = route.query.btn as string;
  return btnVariantMap[btn] || 'default';
});

const btnLabel = computed(() => {
  const labels: Record<string, string> = {
    '1': 'Glass',
    '2': 'Shadow',
    '3': 'Neon',
    '4': 'Shimmer',
    '5': 'Starry',
  };
  const btn = route.query.btn as string;
  return labels[btn] || 'Original';
});
</script>

<template lang="pug">
.scene().w-full.h-full.relative
  //- TresLeches(
    v-if="isShowSettings"
    class="important-fixed important-left-2 important-w-120")
  .html-center(style="z-index: 1").w-full.h-100dvh.flex.flex-center.absolute
      .html-center__mouseover(
        @mouseenter="mouseenter"
        @mouseleave="mouseleave"
        @touchstart="mouseenter"
        @touchend="mouseleave").pa-16
        .flex.flex-col.items-center.gap-4
          // Text
          .flex.flex-col.items-center.gap-2(style="opacity: .7;")
            .text-h5 дизайн и разработка
            h1.alegreya-400.text-black
              | Pt
              span.italic B
              | urg
            .text-h5 арт студия
          // Button
          TreBtn(
            label="Смотреть работы"
            :variant="btnVariant"
            color="white"
            text-color="black"
            @click="goToProjects")
          // AI Chat trigger
          AiChatTrigger


  TresCanvas(
    ref="canvasRef"
    v-bind="gl"
    transparents
    render-mode="manual"
    aria-hidden="true")
    //- Levioso(
      :speed="2"
      :rotation-factor="0.05"
      :float-factor="0.5")
    TresPerspectiveCamera(
        ref="perspectiveCameraRef"
        :position="new Vector3(30, 30, 100)"
        :rotate-x="-Math.PI*0.5"
        :fov="25"
        :zoom="cameraZoomDefault")
    //- OrbitControls(
      :enable-zoom="false")



    //- TresGridHelper(
      :args="[100, 100]"
      :position="[0, 0, -8]"
      :rotate-x="-0.5 * Math.PI")
    // Light
    //- TresHemisphereLight(
      :intensity="0.5"
      ground-color="black")
    //- TresAmbientLight(
      :intensity="1")
    //- TresDirectionalLight(
      :intensity="3"
      :position="[-5,5,5]")

    // Post
    WaterLikePostEffectDS3(ref="waterLikePostEffectDS3Ref" :tier="tier")
    //- WaterColorDiff

    Suspense
      template(#fallback)
        TresGroup
      Ocean(
        v-if="enableOcean"
        ref="oceanRef"
        :position="[0,-0.8,0]"
        :rotation-x="- Math.PI / 2"
        :sun-color="0xffffff"
        :water-color="0x001e0f"
        :distortion-scale="3.7"
        :waterNormals)
    Sky(v-if="enableSky" v-bind="skyProps")

    //- TresGroup(
      ref="centerGroupRef"
      :position="[30, 29, 90]"
      :rotation-x="-Math.PI*0.035")
      Html(
        v-if="route.path === '/'"
        center
        wrapper-class="tres-html-center"
        :z-index-range="[1,2]")
        .html-center().w-100vw.flex.flex-center
          .html-center__mouseover(
            @mouseenter="mouseenter"
            @mouseleave="mouseleave").pa-16
            // Text
            .flex.flex-center
              .html-center__text(style="opacity: .7;")
                h5 дизайн и разработка
                h1.alegreya-400.text-black
                  | Pt
                  span.italic B
                  | urg
                h5 арт студия
            // Button
            .flex.flex-center.min-w-320px
              .pa-8
                TreBtn(
                  label="Смотреть работы"
                  color="white"
                  text-color="black"
                  @click="goToProjects")
              //- .w-full.text-center
                EmailLink.text-black.opacity-60

</template>
